import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  domainForPatternSubstitution,
  resolveMarketplaceDomain,
  substituteDomainInPattern,
} from '../utils/regexPatterns';

interface RegexTesterDialogProps {
  open: boolean;
  onClose: () => void;
  pattern: string;
  title?: string;
  mode?: 'match' | 'replace';
  replacement?: string;
  /** When true, only a match at index 0 counts (same idea as Python re.match / scraper custom post-processing). */
  matchOnlyAtStart?: boolean;
  /** Marketplace domain used to resolve `{domain}` in the pattern before testing. */
  domain?: string;
}

interface MatchResult {
  fullMatch: string;
  groups: string[];
  index: number;
}

/** Mirrors scraper apply_post_processing custom type: group(1) if present else group(0). */
function customPostProcessExtracted(m: RegExpExecArray): string {
  return m.length > 1 && m[1] !== undefined ? m[1] : m[0];
}

/**
 * Translate a Python-style replacement string into JavaScript-style for use with
 * `String.prototype.replace`. The scraper backend uses Python's `re.sub`, which
 * interprets `\1`-`\9` as capture-group back-references and `\g<name>` as a
 * named back-reference. JS uses `$1`-`$9` and `$<name>`, and treats a bare `$`
 * as the start of a back-reference (so a literal `$` must be doubled). This
 * helper keeps the stored value Python-style while making the live preview
 * match what the backend will actually produce.
 */
function pythonReplacementToJs(repl: string): string {
  let out = '';
  let i = 0;
  while (i < repl.length) {
    const c = repl[i];
    if (c === '\\') {
      const next = repl[i + 1];
      // \\ -> literal backslash
      if (next === '\\') {
        out += '\\';
        i += 2;
        continue;
      }
      // \1 .. \9 -> $1 .. $9
      if (next && next >= '1' && next <= '9') {
        out += '$' + next;
        i += 2;
        continue;
      }
      // \g<name> -> $<name>
      if (next === 'g' && repl[i + 2] === '<') {
        const closeIdx = repl.indexOf('>', i + 3);
        if (closeIdx > 0) {
          const name = repl.slice(i + 3, closeIdx);
          out += '$<' + name + '>';
          i = closeIdx + 1;
          continue;
        }
      }
      // Unknown escape — pass the backslash through verbatim
      out += c;
      i++;
      continue;
    }
    // Literal `$` in Python must be escaped to `$$` in a JS replacement
    if (c === '$') {
      out += '$$';
      i++;
      continue;
    }
    out += c;
    i++;
  }
  return out;
}

const RegexTesterDialog: React.FC<RegexTesterDialogProps> = ({
  open,
  onClose,
  pattern,
  title = 'Test Regex Pattern',
  mode = 'match',
  replacement = '',
  matchOnlyAtStart = false,
  domain = '',
}) => {
  const theme = useTheme();
  const [testString, setTestString] = useState('');
  const [localReplacement, setLocalReplacement] = useState(replacement);
  const [domainOverride, setDomainOverride] = useState('');

  const hasDomainPlaceholder = pattern.includes('{domain}');
  const defaultDomain = resolveMarketplaceDomain(domain);
  const substitutionDomain = domainForPatternSubstitution(domain, domainOverride);
  const usingOverride = Boolean(domainOverride.trim());
  const effectivePattern = substituteDomainInPattern(pattern, substitutionDomain);
  const patternWasSubstituted = effectivePattern !== pattern;

  // Reset when dialog opens with a new pattern
  useEffect(() => {
    if (open) {
      setLocalReplacement(replacement);
      setDomainOverride('');
    }
  }, [open, replacement, pattern]);

  // Compute regex result
  const result = useMemo(() => {
    if (!effectivePattern || !testString) {
      return {
        error: null,
        matches: [] as MatchResult[],
        replaced: null,
        isMatch: false,
        extractedValue: null as string | null,
      };
    }

    try {
      if (mode === 'replace') {
        const regex = new RegExp(effectivePattern, 'g');
        const jsReplacement = pythonReplacementToJs(localReplacement);
        const replaced = testString.replace(regex, jsReplacement);
        return {
          error: null,
          matches: [],
          replaced,
          isMatch: regex.test(testString),
          extractedValue: null,
        };
      }

      // Match mode — anchored at start (Python re.match / scraper custom_pattern)
      if (matchOnlyAtStart) {
        const regex = new RegExp(effectivePattern);
        const m = regex.exec(testString);
        const isMatch = Boolean(m && m.index === 0);
        const matches: MatchResult[] = [];
        let extractedValue: string | null = null;
        if (isMatch && m) {
          matches.push({
            fullMatch: m[0],
            groups: m.slice(1),
            index: m.index,
          });
          extractedValue = customPostProcessExtracted(m);
        }
        return { error: null, matches, replaced: null, isMatch, extractedValue };
      }

      // Match mode — find all occurrences (default)
      const regex = new RegExp(effectivePattern, 'g');
      const matches: MatchResult[] = [];
      let match;

      regex.lastIndex = 0;

      while ((match = regex.exec(testString)) !== null) {
        matches.push({
          fullMatch: match[0],
          groups: match.slice(1),
          index: match.index,
        });

        if (match.index === regex.lastIndex) {
          regex.lastIndex++;
        }
      }

      const simpleRegex = new RegExp(effectivePattern);
      const isMatch = simpleRegex.test(testString);

      return {
        error: null,
        matches,
        replaced: null,
        isMatch,
        extractedValue: null,
      };
    } catch (e) {
      return {
        error: e instanceof Error ? e.message : 'Invalid regex pattern',
        matches: [] as MatchResult[],
        replaced: null,
        isMatch: false,
        extractedValue: null,
      };
    }
  }, [effectivePattern, testString, mode, localReplacement, matchOnlyAtStart]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Current Pattern Display */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Pattern
            </Typography>
            <Box
              sx={{
                p: 1.5,
                bgcolor: theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.common.white, 0.05)
                  : alpha(theme.palette.common.black, 0.05),
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                wordBreak: 'break-all',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              {pattern || <em>No pattern specified</em>}
            </Box>
            {hasDomainPlaceholder && (
              <TextField
                fullWidth
                sx={{ mt: 1.5 }}
                label="Domain for {domain}"
                value={domainOverride}
                onChange={e => setDomainOverride(e.target.value)}
                placeholder={defaultDomain || 'e.g. shopee.ph'}
                helperText={
                  defaultDomain
                    ? `Optional override. If empty, uses definition domain: ${defaultDomain}`
                    : 'Enter the host to substitute for {domain} in this pattern'
                }
                size="small"
              />
            )}
            {patternWasSubstituted && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Resolved pattern ({'{domain}'} → {substitutionDomain}
                  {usingOverride ? ', override' : defaultDomain ? ', from definition' : ''})
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    wordBreak: 'break-all',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  }}
                >
                  {effectivePattern}
                </Box>
              </Box>
            )}
            {hasDomainPlaceholder && !substitutionDomain && (
              <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                <Typography variant="body2">
                  Pattern contains {'{domain}'} but no domain is available. Enter a domain above or
                  set the Domain field on this definition.
                </Typography>
              </Alert>
            )}
          </Box>

          {/* Test String Input */}
          {matchOnlyAtStart && mode === 'match' && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="body2">
                Match must start at the beginning of the test string (same rule as Python{' '}
                <code style={{ fontSize: '0.85em' }}>re.match</code> used by the scraper for custom
                post-processing).
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth
            label="Test String"
            value={testString}
            onChange={(e) => setTestString(e.target.value)}
            placeholder="Enter a string to test against the regex pattern"
            multiline
            rows={3}
            autoFocus={!hasDomainPlaceholder}
          />

          {/* Replacement Input (for replace mode) */}
          {mode === 'replace' && (
            <TextField
              fullWidth
              label="Replacement"
              value={localReplacement}
              onChange={(e) => setLocalReplacement(e.target.value)}
              placeholder="Replacement string (use \1, \2 for capture groups — Python syntax)"
              size="small"
            />
          )}

          <Divider />

          {/* Results */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Results
            </Typography>

            {/* Error Display */}
            {result.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Invalid Pattern:</strong> {result.error}
                </Typography>
              </Alert>
            )}

            {/* No input yet */}
            {!testString && !result.error && (
              <Typography variant="body2" color="text.secondary">
                Enter a test string above to see results.
              </Typography>
            )}

            {/* Match Results */}
            {testString && !result.error && mode === 'match' && (
              <Box>
                {/* Overall Match Status */}
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={result.isMatch ? 'MATCHES' : 'NO MATCH'}
                    color={result.isMatch ? 'success' : 'error'}
                    size="small"
                  />
                </Box>

                {matchOnlyAtStart &&
                  result.isMatch &&
                  result.extractedValue !== null &&
                  result.extractedValue !== undefined && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Scraper custom post-processing would set field to:
                      </Typography>
                      <Box
                        sx={{
                          p: 1,
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                        }}
                      >
                        {result.extractedValue}
                      </Box>
                    </Box>
                  )}

                {/* Detailed Matches */}
                {result.matches.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {result.matches.map((match, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1.5,
                          bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.15 : 0.1),
                          border: '1px solid',
                          borderColor: alpha(theme.palette.success.main, 0.3),
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          <strong>Match {idx + 1}:</strong> "{match.fullMatch}"
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (at index {match.index})
                          </Typography>
                        </Typography>
                        {match.groups.length > 0 && (
                          <Box sx={{ mt: 1, pl: 2 }}>
                            {match.groups.map((group, gIdx) => (
                              <Typography key={gIdx} variant="body2" sx={{ fontFamily: 'monospace' }}>
                                Group {gIdx + 1}: "{group}"
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  result.isMatch && (
                    <Typography variant="body2" color="text.secondary">
                      Pattern matches the test string (no capture groups).
                    </Typography>
                  )
                )}
              </Box>
            )}

            {/* Replace Results */}
            {testString && !result.error && mode === 'replace' && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  <strong>Original:</strong>
                </Typography>
                <Box
                  sx={{
                    p: 1,
                    bgcolor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.common.white, 0.05)
                      : alpha(theme.palette.common.black, 0.05),
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    mb: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {testString}
                </Box>
                <Typography variant="body2" gutterBottom>
                  <strong>After Replacement:</strong>
                </Typography>
                <Box
                  sx={{
                    p: 1,
                    bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.15 : 0.1),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.success.main, 0.3),
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  }}
                >
                  {result.replaced}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegexTesterDialog;
